import { useEffect, useState } from 'react';
import { api } from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Coverholder {
  company_id: string;
  company_name: string;
  dba_name: string;
  claim_count: number;
  active_yn: string;
}

interface LossRunResult {
  file_base64: string;
  file_name: string;
  company_name: string;
  claim_count: number;
  group_count: number;
  cutoff_date: string;
  status_filter: string;
  generated_at: string;
  message?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getTodayDate(): string {
  return new Date().toISOString().split('T')[0];
}

function downloadBase64File(base64: string, fileName: string) {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(link.href);
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function LossRunGenerator() {
  const [coverholders, setCoverholders] = useState<Coverholder[]>([]);
  const [loadingCoverholders, setLoadingCoverholders] = useState(true);
  const [coverholderError, setCoverholderError] = useState<string | null>(null);

  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [cutoffDate, setCutoffDate] = useState(getTodayDate());
  const [statusFilter, setStatusFilter] = useState<'open' | 'closed' | 'all'>('open');

  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [result, setResult] = useState<LossRunResult | null>(null);

  const selectedCoverholder = coverholders.find(
    (c) => c.company_id === selectedCompanyId
  );

  // ── Load coverholders on mount ─────────────────────────────────────────────
  useEffect(() => {
    setLoadingCoverholders(true);
    setCoverholderError(null);

    api
      .getLossRunCoverholders()
      .then((data: { coverholders: Coverholder[]; count: number }) => {
        setCoverholders(data.coverholders);
        if (data.coverholders.length > 0) {
          setSelectedCompanyId(data.coverholders[0].company_id);
        }
      })
      .catch((err: Error) => {
        setCoverholderError(err.message || 'Failed to load coverholders.');
      })
      .finally(() => {
        setLoadingCoverholders(false);
      });
  }, []);

  // ── Generate Loss Run ──────────────────────────────────────────────────────
  async function handleGenerate(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedCompanyId) return;

    setGenerating(true);
    setGenerateError(null);
    setResult(null);

    try {
      const data: LossRunResult = await api.generateLossRun(
        selectedCompanyId,
        cutoffDate,
        statusFilter
      );

      if (data.message && !data.file_base64) {
        // No claims found — show as info, not error
        setResult(data);
      } else {
        setResult(data);
      }
    } catch (err: any) {
      setGenerateError(err.message || 'Loss run generation failed.');
    } finally {
      setGenerating(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="p-8 max-w-3xl mx-auto">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
          Loss Run Generator
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Generate loss run reports for coverholders, brokers, and insureds
        </p>
      </div>

      {/* Form card */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-5">
          Report Parameters
        </h2>

        {loadingCoverholders ? (
          <div className="py-8">
            <LoadingSpinner message="Loading coverholders..." />
          </div>
        ) : coverholderError ? (
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            <span className="font-semibold">Error loading coverholders:</span>{' '}
            {coverholderError}
          </div>
        ) : (
          <form onSubmit={handleGenerate} className="space-y-5">
            {/* Coverholder */}
            <div>
              <label
                htmlFor="coverholder"
                className="block text-sm font-medium text-slate-700 mb-1.5"
              >
                Coverholder
              </label>
              <select
                id="coverholder"
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
                disabled={generating}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
              >
                {coverholders.length === 0 && (
                  <option value="">No coverholders available</option>
                )}
                {coverholders.map((ch) => (
                  <option key={ch.company_id} value={ch.company_id}>
                    {ch.company_name}
                    {ch.dba_name && ch.dba_name !== ch.company_name
                      ? ` (${ch.dba_name})`
                      : ''}{' '}
                    — {ch.claim_count.toLocaleString()} claim
                    {ch.claim_count !== 1 ? 's' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Cutoff date and status filter row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="cutoff-date"
                  className="block text-sm font-medium text-slate-700 mb-1.5"
                >
                  Cutoff Date
                </label>
                <input
                  id="cutoff-date"
                  type="date"
                  value={cutoffDate}
                  onChange={(e) => setCutoffDate(e.target.value)}
                  disabled={generating}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Claim Status
                </label>
                <div className="flex gap-4 pt-1.5">
                  {(['open', 'closed', 'all'] as const).map((opt) => (
                    <label
                      key={opt}
                      className="flex items-center gap-1.5 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="statusFilter"
                        value={opt}
                        checked={statusFilter === opt}
                        onChange={() => setStatusFilter(opt)}
                        disabled={generating}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-700 capitalize">
                        {opt}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Generate button */}
            <div className="pt-1">
              <button
                type="submit"
                disabled={generating || !selectedCompanyId}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors duration-150"
              >
                {generating ? (
                  <>
                    <span
                      className="w-4 h-4 rounded-full border-2 border-blue-300 border-t-white animate-spin flex-shrink-0"
                      aria-hidden="true"
                    />
                    Generating...
                  </>
                ) : (
                  'Generate Loss Run'
                )}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Progress state */}
      {generating && selectedCoverholder && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center gap-4">
            <div
              className="w-8 h-8 rounded-full border-4 border-blue-200 border-t-blue-500 animate-spin flex-shrink-0"
              role="status"
              aria-label="Generating Loss Run"
            />
            <div>
              <p className="text-sm font-medium text-slate-700">
                Generating loss run for{' '}
                <span className="text-blue-600">
                  {selectedCoverholder.company_name}
                </span>
                ...
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                Querying claims and aggregating financials.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error state */}
      {generateError && (
        <div className="rounded-xl bg-red-50 border border-red-200 px-5 py-4 mb-6">
          <p className="text-sm font-semibold text-red-800 mb-0.5">
            Generation failed
          </p>
          <p className="text-sm text-red-700">{generateError}</p>
        </div>
      )}

      {/* No claims result */}
      {result && !generating && result.message && !result.file_base64 && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 px-5 py-4 mb-6">
          <p className="text-sm font-semibold text-amber-800 mb-0.5">
            No Claims Found
          </p>
          <p className="text-sm text-amber-700">{result.message}</p>
        </div>
      )}

      {/* Success card */}
      {result && !generating && result.file_base64 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Success header */}
          <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100 bg-green-50">
            <div
              className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0"
              aria-hidden="true"
            >
              <svg
                className="w-4 h-4 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800">
                Loss Run Generated Successfully
              </p>
              <p className="text-xs text-slate-500 mt-0.5">{result.file_name}</p>
            </div>
          </div>

          {/* Summary stats */}
          <div className="px-6 py-5">
            <div className="grid grid-cols-2 gap-4 mb-5">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Coverholder
                </p>
                <p className="mt-1 text-sm font-semibold text-slate-800">
                  {result.company_name}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Cutoff Date
                </p>
                <p className="mt-1 text-sm font-semibold text-slate-800">
                  {result.cutoff_date}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Claims
                </p>
                <p className="mt-1 text-sm font-semibold text-slate-800">
                  {result.claim_count.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Contract Groups
                </p>
                <p className="mt-1 text-sm font-semibold text-slate-800">
                  {result.group_count}
                </p>
              </div>
            </div>

            {/* Status filter display */}
            <div className="mb-5">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                Status Filter
              </p>
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700 capitalize">
                {result.status_filter}
              </span>
            </div>

            {/* Download button */}
            <button
              onClick={() => downloadBase64File(result.file_base64, result.file_name)}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-150"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Download Excel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
